import { Button, ModuleHeader } from '@/components'
import { Input } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { PermissionsRequired } from '@/containers/permission-containers'
import { PageType } from '@/modules/common/consts'
import { useAdmin, useCompany, useDebounce } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useState } from 'react'
import { BrowserView, MobileView } from 'react-device-detect'
import styled from 'styled-components'
import {
  ExportFormat,
  ExportOption,
  USER_EXPORT_STATUS_MAP,
  type UserExportStatusType,
} from '../exports'
import { ExportEquipmentModal } from '../vehicles/fleet/components/ExportEquipmentModal'
import { useUserExportMutations } from './mutations.hooks'
import { UsersTable } from './UsersTable'
import { UserDocumentCategoryFilter } from './UsersTable/UserDocumentCategoryFilter'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`
const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;
  }
`

export type Metrics = {
  totalCount: number
  missingConfigCount: number
  activeUserCount: number
}

const Users: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics>({
    totalCount: 0,
    missingConfigCount: 0,
    activeUserCount: 0,
  })

  const translations = useTranslate({
    users: 'common.users',
    newUser: 'users.new',
    searchForUser: 'users.search',
    export: 'common.export',
    showDeactivated: 'users.show-deactivated',
    usersCount: 'users.total-count',
    usersMissingConfig: 'users.missing-configuration-count',
    usersActiveCount: 'users.active-user-count',
    showOnlyDeactivated: 'users.show-only-deactivated',
  })

  const isAdmin = useAdmin()
  const addPrompt = usePrompt()
  const company = useCompany()
  const { exportUsers, loading } = useUserExportMutations()
  const { formValues: filter, updateForm } = useForm<{
    query: string
    sort: string
    showDeactivated: boolean
    showOnlyDeactivated: boolean
    includeExpiredDocuments: boolean
    documentCategorySelection: string[]
  }>({
    values: {
      query: '',
      sort: 'lastName',
      showDeactivated: false,
      showOnlyDeactivated: false,
      includeExpiredDocuments: false,
      documentCategorySelection: [],
    },
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'users-controls',
        retrieveFormStateOnMount: true,
        excludeFields: ['query', 'documentCategorySelection'],
      },
    },
  })

  const debouncedQuery = useDebounce(filter.query)

  async function doExport(values: {
    exportFormat: ExportFormat
    statusType: UserExportStatusType
  }) {
    await exportUsers({
      variables: {
        exportType: 'users',
        company: company.id,
        exportFormat: values.exportFormat,
        exportOption:
          values.exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
        userFilter: {
          status: USER_EXPORT_STATUS_MAP[values.statusType],
        },
      },
    })
  }

  async function handleExport() {
    const { data } = await addPrompt<{
      exportFormat: ExportFormat
      statusType: UserExportStatusType
    } | null>(resolve => <ExportEquipmentModal onSubmit={resolve} />)
    if (!data) return

    void doExport(data)
  }

  // Callback to update metrics state from child component
  const updateMetrics = useCallback((newMetrics: Partial<Metrics>) => {
    setMetrics(metrics => ({ ...metrics, ...newMetrics }))
  }, [])

  return (
    <Wrapper>
      <ModuleHeader title={translations.users} page={PageType.USERS} />
      <div className="flex justify-between align-middle mb-4 flex-wrap gap-2">
        <div className="flex flex-col justify-center [&>p]:font-semibold">
          <p>
            {translations.usersCount}: {metrics.totalCount}
          </p>
          <p>
            {translations.usersMissingConfig}: {metrics.missingConfigCount}
          </p>
          <p>
            {translations.usersActiveCount}: {metrics.activeUserCount}
          </p>
        </div>
        {isAdmin && (
          <Button disabled={loading} onClick={handleExport}>
            <Icon icon="download" />
            {translations.export}
          </Button>
        )}
        <MobileView>
          <PermissionsRequired permissions="users.add_user">
            <Button asChild size="icon">
              <PlainLink to="/users/create">
                <Icon icon="plus" />
              </PlainLink>
            </Button>
          </PermissionsRequired>
        </MobileView>
      </div>

      <Controls>
        <Input
          value={filter.query}
          type="search"
          placeholder={translations.searchForUser}
          onChange={value => updateForm({ query: value })}
        />

        <PermissionsRequired permissions={PERMISSIONS.users.add.user}>
          <UserDocumentCategoryFilter
            showDeactivated={filter.showDeactivated}
            showOnlyDeactivated={filter.showOnlyDeactivated}
            includeExpiredDocuments={filter.includeExpiredDocuments}
            onSelectionChange={value =>
              updateForm({
                documentCategorySelection: value,
              })
            }
            onShowDeactivatedChange={value =>
              updateForm({
                showDeactivated: value,
                showOnlyDeactivated: false,
              })
            }
            onShowOnlyDeactivatedChange={value =>
              updateForm({
                showOnlyDeactivated: value,
                showDeactivated: false,
              })
            }
            onIncludeExpiredDocumentsChange={value =>
              updateForm({
                includeExpiredDocuments: value,
              })
            }
          />
        </PermissionsRequired>

        <BrowserView>
          <PermissionsRequired permissions={PERMISSIONS.users.add.user}>
            <Button asChild>
              <PlainLink to="/users/create">
                <Icon icon="plus" />
                {translations.newUser}
              </PlainLink>
            </Button>
          </PermissionsRequired>
        </BrowserView>
      </Controls>
      <UsersTable
        query={debouncedQuery}
        sort={filter.sort}
        showDeactivated={filter.showDeactivated}
        showOnlyDeactivated={filter.showOnlyDeactivated}
        includeExpiredDocuments={filter.includeExpiredDocuments}
        documentCategorySelection={filter.documentCategorySelection}
        onSort={value => updateForm({ sort: value })}
        updateMetrics={updateMetrics}
      />
    </Wrapper>
  )
}

export default Users
